export type Nis2ControlStatus = 'IMPLEMENTED' | 'PARTIAL' | 'NOT_IMPLEMENTED' | 'N_A';
export type Nis2Domain = 'Risk Management' | 'Incident Handling' | 'Business Continuity' | 'Supply Chain';

export interface Nis2Control {
  standard: string;
  controlCode: string;
  domain: Nis2Domain;
  title: { en: string; es: string; ru: string };
  description: { en: string; es: string; ru: string };
  severity: 'CRITICAL' | 'MAJOR' | 'MODERATE';
  evidence: { type: string; examples: { en: string[]; es: string[]; ru: string[] } }[];
  vendorImpact: { applies: boolean };
  governanceLinks: { policies: string[]; processes: string[]; roles: string[] };
  crossMapping: { ISO27001: string[]; DORA: string[]; ENS_ES: string[] };
  recommendations: { CRITICAL?: { en: string; es: string; ru: string }; MAJOR?: { en: string; es: string; ru: string } };
}

const ev = (t: string, en: string[], es: string[], ru: string[]) => ({ type: t, examples: { en, es, ru } });

function def(code: string, domain: Nis2Domain, title: { en: string; es: string; ru: string }, desc: { en: string; es: string; ru: string }, severity: 'CRITICAL' | 'MAJOR', evidence: Nis2Control['evidence'], vendor: boolean, rec: Partial<Nis2Control['recommendations']>): Nis2Control {
  return {
    standard: 'NIS2', controlCode: `NIS2_${code}`, domain, title, description: desc, severity, evidence,
    vendorImpact: { applies: vendor }, governanceLinks: { policies: [], processes: [], roles: [] },
    crossMapping: { ISO27001: [], DORA: [], ENS_ES: [] }, recommendations: rec,
  };
}

const NIS2_RM = [
  def('RM_01', 'Risk Management', { en: 'Risk management (Art.21)', es: 'Gestión de riesgos', ru: 'Управление рисками' },
    { en: 'Cybersecurity risk management implemented.', es: 'Gestión de riesgos implementada.', ru: 'Управление киберрисками реализовано.' },
    'CRITICAL', [ev('policy', ['Risk policy'], ['Política'], ['Политика'])], false,
    { CRITICAL: { en: 'Implement NIS2 risk management.', es: 'Implementar gestión de riesgos NIS2.', ru: 'Внедрить управление рисками NIS2.' } }),
  def('RM_02', 'Risk Management', { en: 'Asset identification', es: 'Identificación activos', ru: 'Идентификация активов' },
    { en: 'Critical assets identified.', es: 'Activos críticos identificados.', ru: 'Критические активы выявлены.' },
    'CRITICAL', [ev('record', ['Inventory'], ['Inventario'], ['Реестр'])], false,
    { CRITICAL: { en: 'Identify critical assets.', es: 'Identificar activos críticos.', ru: 'Выявить критические активы.' } }),
];
const NIS2_IH = [
  def('IH_01', 'Incident Handling', { en: 'Incident handling (Art.23)', es: 'Gestión incidentes', ru: 'Обработка инцидентов' },
    { en: 'Incident detection and reporting.', es: 'Detección y reporte de incidentes.', ru: 'Выявление и сообщение об инцидентах.' },
    'CRITICAL', [ev('procedure', ['IR procedures'], ['Procedimientos'], ['Процедуры'])], false,
    { CRITICAL: { en: 'Implement incident handling.', es: 'Implementar gestión de incidentes.', ru: 'Внедрить обработку инцидентов.' } }),
];
const NIS2_BC = [
  def('BC_01', 'Business Continuity', { en: 'BC/DR (Art.21)', es: 'BC/DR', ru: 'BC/DR' },
    { en: 'BC/DR measures implemented.', es: 'Medidas BC/DR implementadas.', ru: 'Меры BC/DR реализованы.' },
    'CRITICAL', [ev('record', ['BCP'], ['BCP'], ['BCP'])], false,
    { CRITICAL: { en: 'Implement BC/DR.', es: 'Implementar BC/DR.', ru: 'Внедрить BC/DR.' } }),
];
const NIS2_SC = [
  def('SC_01', 'Supply Chain', { en: 'Supply chain security', es: 'Seguridad cadena suministro', ru: 'Безопасность цепочки' },
    { en: 'Supply chain security.', es: 'Seguridad de cadena de suministro.', ru: 'Безопасность цепочки поставок.' },
    'MAJOR', [ev('policy', ['Vendor policy'], ['Política proveedores'], ['Политика'])], true,
    { MAJOR: { en: 'Implement supply chain security.', es: 'Implementar seguridad cadena.', ru: 'Внедрить безопасность цепочки.' } }),
];

export const NIS2_CONTROLS_CATALOG: Nis2Control[] = [...NIS2_RM, ...NIS2_IH, ...NIS2_BC, ...NIS2_SC];
const DOMAIN_ORDER: Nis2Domain[] = ['Risk Management', 'Incident Handling', 'Business Continuity', 'Supply Chain'];
export function getNis2ControlsByDomain(domain: Nis2Domain): Nis2Control[] {
  return NIS2_CONTROLS_CATALOG.filter((c) => c.domain === domain);
}
export function getNis2ControlDomains(): Nis2Domain[] {
  return DOMAIN_ORDER;
}
